<template>
  <v-dialog max-width="1200px" v-model="open">
    <v-form ref="form" @submit.prevent="submit">
      <validation-observer ref="observer" v-slot="{ invalid }">
        <v-card>
          <v-card-title>
            <span class="headline">{{ templateType.id ? $t('EmailTypeDialog.EditEmailTemplateType') : $t('EmailTypeDialog.CreateEmailTemplateType') }}</span>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12">
                  <validation-provider v-slot="{ errors }" name="Name" rules="required">
                    <v-text-field v-model="templateType.name" :label="$t('GeneralTranslations.Name')+'*'" :error-messages="errors"></v-text-field>
                  </validation-provider>

                  <v-select class="pt-3" :label="$t('EmailTypeDialog.MailingList')" @change="mailingListChange"
                    v-model="selectedMailingListId" item-text="name" item-value="id"
                    :items="facilityMailingListsOptions"></v-select>

                  <v-checkbox :label="$t('EmailTypeDialog.AddTrigger')" v-model="hasRules" @change="addTriggerChange"></v-checkbox>
                </v-col>
              </v-row>
              <v-row v-if="hasRules && rule && rule.trigger" no-gutters>
                <v-col cols="2" class="pr-1">
                  <validation-provider v-slot="{ errors }" name="Send after" rules="required|numeric">
                    <v-text-field type="number" :prefix="$t('EmailTypeDialog.SendAfter')" v-model="rule.sendPeriod.after"
                      :error-messages="errors" required>
                    </v-text-field>
                  </validation-provider>
                </v-col>
                <v-col cols="1">
                  <v-select v-model="rule.sendPeriod.period" :items="dateTimePeriods" item-text="text" item-value="value">
                    <template slot="item" slot-scope="data">{{ $t(`GeneralTranslations.${data.item.text}`) }}</template>
                    <template slot="selection" slot-scope="data">{{  $t(`GeneralTranslations.${data.item.text}`) }}</template>
                  </v-select>
                </v-col>
                <v-col cols="3" class="pl-1">
                  <v-select :label="$t('EmailTypeDialog.ThisHappens')" v-model="rule.trigger.event" item-text="text" item-value="value" :items="triggerEvents"
                    @change="onTriggerEventSelect">
                    <template slot="item" slot-scope="data">
                      {{ $t(`TriggerEventEnum.${data.item.text}`) }} 
                    </template>
                    <template slot="selection" slot-scope="data">
                      <span v-if="triggerEventItemId?.userType === userTypeEnum.Resident">
                         {{$t('EmailTypeDialog.ResidentIsAdmitted/Departed')}}
                      </span>
                      <span v-else>
                        {{ $t(`TriggerEventEnum.${data.item.text}`) }} 
                      </span>
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="3" class="pl-1" v-if="triggerEventItems && triggerEventItems.length">
                  <validation-provider v-slot="{ errors }" name="Trigger" rules="required">
                    <v-select required :label="itemsLabels" v-model="triggerEventItemId" item-value="id"
                      :items="triggerEventItems" @change="onTriggerEventItemSelect" :error-messages="errors">
                      <template slot="selection" slot-scope="data">
                        {{ data.item.name }}
                        <span class="ml-2 defaultTypeMark" v-if="data.item.isDefault">{{$t('GeneralTranslations.default')}}</span>
                      </template>
                      <template slot="item" slot-scope="data">
                        {{ data.item.name }}
                        <span class="ml-2 defaultTypeMark" v-if="data.item.isDefault">{{$t('GeneralTranslations.default')}}</span>
                      </template>
                    </v-select>
                  </validation-provider>
                </v-col>
                <v-col cols="3" class="pl-1" v-if="showDocumentTriggers && triggerEventItemId">
                  <validation-provider v-slot="{ errors }" name="Action" rules="required">
                    <v-radio-group required class="mt-2 pl-2" row v-model="rule.trigger.data.documentTrigger"
                      @change="onDocumentTriggerChange" :error-messages="errors">
                      <v-radio :label="triggerEventItemId?.userType === userTypeEnum.Resident ? $t('GeneralTranslations.Admitted') : $t('GeneralTranslations.Added')"
                        value="added"></v-radio>
                      <v-radio :label="triggerEventItemId?.userType === userTypeEnum.Resident ? $t('GeneralTranslations.Departed') : $t('GeneralTranslations.Deleted')"
                        value="deleted"></v-radio>
                    </v-radio-group>
                  </validation-provider>
                </v-col>
              </v-row>
                <v-row class="mt-0" v-if="hasRules">
                <v-col cols="12">
                  <v-checkbox :label="$t('EmailTypeDialog.AlsoSendToNewUser')" v-model="applyToNewUsers" @change="applyToNewUsersChange"></v-checkbox>
                </v-col>
              </v-row>
            </v-container>
            <small>{{$t('GeneralTranslations.StarIndicatesRequiredField')}}</small>
          </v-card-text>

          <v-card-actions>
            <v-btn v-if="templateType.id" color="blue darken-1" text @click="removeClick">
              {{$t('GeneralTranslations.Delete')}}
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="close">
              {{$t('GeneralTranslations.Cancel')}}
            </v-btn>

            <v-btn color="blue darken-1" text :disabled="invalid" :loading="loading" type="submit">
              {{ templateType.id ? $t('GeneralTranslations.Save') : $t('GeneralTranslations.Create')}}
            </v-btn>
          </v-card-actions>
        </v-card>
      </validation-observer>
    </v-form>
    <ConfirmDlg ref="confirmationModal"></ConfirmDlg>
  </v-dialog>
</template>

<script>
import { mapState } from "vuex";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import ConfirmDlg from "@/components/popup/confirm-dialog/ConfirmDlg.vue";
import { StaffAccessLevels } from "@/enums/StaffAccessLevels";
import { UserTypeEnum } from "@/enums/UserTypeEnum";
import { EventsTypesOptions } from "@/consts/EventsTypesOptions";
import { PremiumServiceEventTypes } from "@/enums/PremiumServiceEventTypes";
import { DefaultEmailTemplateType } from "@/enums/DefaultEmailTemplateType";
import { toTranslationKey } from "@/utils/stringFormat";
import { fixString } from '@/utils/fixString';

import {
  DateTimePeriodConditionModel, DateTimePeriodEnum, TriggerEventEnum, ActionTypeEnum, PropertiesConditionModel
} from "@/businessRules/BusinessRulesModels";


export default {
  components: {
    ValidationObserver,
    ValidationProvider,
    ConfirmDlg
  },
  props: {
    templateType: {
      type: Object,
      default: () => ({ name: '' })
    },
    value: Boolean
  },
  data() {
    return {
      loading: false,
      selectedMailingListId: null,
      facilityMailingListsOptions: [{ id: null, name: this.$t('GeneralTranslations.NotSelected') }],
      hasRules: false,
      applyToNewUsers: false,
      triggerEventItemId: null,
      rule: {},
      dateTimePeriods: Object.values(DateTimePeriodEnum).map(x=>({text: toTranslationKey(x), value: x})),
      triggerEvents: Object.values(TriggerEventEnum).map(x=>({text: toTranslationKey(x), value: x})),
      userTypes: [{ name: this.$t('EmailTypeDialog.Staff-OrgAdmin'), userType: 'staff', accessLevel: StaffAccessLevels.OrgAdmin, collection: 'users' }, { name: this.$t('EmailTypeDialog.Staff-FacilityAdmin'), userType: UserTypeEnum.Staff, accessLevel: StaffAccessLevels.FacilityAdmin, collection: 'users' }, { name: this.$t('EmailTypeDialog.Resident'), userType: UserTypeEnum.Resident, collection: 'residents' }, { name: this.$t('EmailTypeDialog.FamilyMember'), userType: UserTypeEnum.FamilyHubMember, collection: 'familyHubUsers' }],
      eventsTypesOptions: [],
      premiumServiceTypesOptions: [],
      triggerEventItems: [],
      propertiesCondition: PropertiesConditionModel,
      dateTimePeriodCondition: DateTimePeriodConditionModel,
      showDocumentTriggers: false,
      currentTriggerEvent: null,
      defaultEmailTemplateType: Object.values(DefaultEmailTemplateType).map((x) => { return { id: x, name: x, isDefault: true } }),
      userTypeEnum: UserTypeEnum
    }
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        this.selectedMailingListId = this.templateType.mailingListIds ? this.templateType.mailingListIds[0] : null
        this.hasRules = (this.templateType.rulesIds && this.templateType.rulesIds.length > 0) ? true : false
        this.loadTemplateTypeData()
      }
      else {
        this.selectedMailingListId = null
      }
    },
    facilityMailingLists(lists) {
      if (lists && lists.length > 0) {
        this.facilityMailingListsOptions = this.facilityMailingListsOptions.concat(lists)
      }
    },
    emailTemplateTypeRule(rule) {
      if (rule && rule.trigger) {
        this.rule = rule
        this.applyToNewUsers = this.rule.actions[0]?.applyToNewUsers || false
        this.populateTriggerEventItems(this.rule.trigger.event)
      }
    }
  },
  computed: {
    ...mapState({
      facilityMailingLists: state => state.facilityModule.facilityMailingLists,
      facilityEmailTemplateTypes: state => state.facilityModule.facilityEmailTemplateTypes,
      emailTemplateTypeRule: state => state.businessRulesModule.rule,
    }),
    open: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    itemsLabels: {
      get() {
        if (this.currentTriggerEvent === TriggerEventEnum.whenEmailTemplateTypeSent) {
          return this.$t('EmailTypeDialog.SelectEmailTemplateType')
        }
        else if (this.currentTriggerEvent === TriggerEventEnum.whenUserAddedDeleted) {
          return this.$t('EmailTypeDialog.SelectUserType')
        }
        else if (this.currentTriggerEvent === TriggerEventEnum.whenCalenderEventAddedDeleted) {
          return this.$t('EmailTypeDialog.SelectEventType')
        }
        else if (this.currentTriggerEvent === TriggerEventEnum.whenPremiumServiceAddedDeleted) {
          return this.$t('EmailTypeDialog.SelectEventType')
        }
        else {
          return this.$t('EmailTypeDialog.SelectItem')
        }
      }
    }
  },
  created() {
    this.eventsTypesOptions = EventsTypesOptions.map(x=>({value: x.value, name: this.$t(x.name)}))

    this.premiumServiceTypesOptions = Object.values(PremiumServiceEventTypes).map(x => ({ value: x, name: this.$t(`PremiumServiceEventTypes.${toTranslationKey(x)}`) }))

    this.$store.dispatch("getFacilityMailingLists")
  },
  methods: {
    async loadTemplateTypeData() {
      if (this.templateType.rulesIds && this.templateType.rulesIds[0]) {
        this.$store.dispatch("getRuleById", this.templateType.rulesIds[0])
      }
    },

    async submit() {
      this.loading = true;

      try {

        if (!this.hasRules && this.rule?.id?.length) {
          await this.$store.dispatch("deleteRule", this.rule.id)
          this.templateType.rulesIds = this.templateType.rulesIds.filter(id => id !== this.rule.id)
          this.rule = null
        }

        if (this.templateType.id) {
          if (this.hasRules && this.rule.id) {
            this.rule.name = this.generateRuleName()
            await this.$store.dispatch("updateRule", this.rule)
            await this.$store.dispatch("updateEmailTemplateType", this.templateType)
          }
          else if (this.hasRules && !this.rule.id) {
            this.rule.name = this.generateRuleName()
            await this.$store.dispatch("addRule", this.rule).then(async (res) => {
              this.rule.id = res.id
              this.templateType.rulesIds = this.templateType.rulesIds || []
              this.templateType.rulesIds.push(res.id)
              await this.$store.dispatch("updateEmailTemplateType", this.templateType)
            })
          }
          else {
            await this.$store.dispatch("updateEmailTemplateType", this.templateType)
          }
        }
        else {
          if (this.hasRules) {
            this.rule.name = this.generateRuleName()
            await this.$store.dispatch("addRule", this.rule).then(async (res) => {
              this.rule.id = res.id
              this.templateType.rulesIds = this.templateType.rulesIds || []
              this.templateType.rulesIds.push(res.id)
              await this.$store.dispatch("createEmailTemplateType", this.templateType).then(async (t) => {
                this.rule.actions[0].data.emailTemplateTypeId = t.id
                this.rule.name = this.generateRuleName()
                await this.$store.dispatch("updateRule", this.rule)
              })
            })
          }
          else {
            await this.$store.dispatch("createEmailTemplateType", this.templateType)
          }
        }

      } catch (error) {
        console.error(error)
      }

      this.loading = false
      this.close()
    },

    async removeClick() {
      if (this.templateType.isDefault) return;

      await this.$refs.confirmationModal.open(null, this.$i18n.messages[this.$i18n.locale]['EmailTypeDialog.DeleteMessage'].replace('[templateType]',this.templateType.name), false)
        .then(async response => {
          if (response && response.agree) {
            await this.$store.dispatch("deleteEmailTemplateType", this.templateType.id)
            this.close()
          }
        })
    },

    close() {
      if (this.$refs.form) {
        this.$refs.form.reset()
        this.rule = null
        this.hasRules = false
        this.templateType = null
        this.triggerEventItemId = null
        this.currentTriggerEvent = null
        this.triggerEventItems = null
        this.triggerEventItemId = null
        this.showDocumentTriggers = false
      }
      this.open = false
    },

    mailingListChange(id) {
      if (id) {
        this.templateType.mailingListIds = [id]
        this.selectedMailingListId = id
      }
      else {
        this.templateType.mailingListIds = []
        this.selectedMailingListId = null
      }
    },

    addTriggerChange(value) {
      if (value) {
        this.rule = {
          name: '',
          trigger: {
            event: '',
            data: {}
          },
          sendPeriod: {
            after: 1,
            period: DateTimePeriodEnum.day
          },
          actions: [{
            type: ActionTypeEnum.sendEmailTemplateType,
            data: { emailTemplateTypeId: this.templateType.id },
            applyToNewUsers: false
          }],
        }
      }
    },

    applyToNewUsersChange(value){
      if(typeof value != "boolean") return

      this.rule.actions[0].applyToNewUsers = value
    },

    generateRuleName() {
      return `${this.templateType.name} email type Trigger`
    },

    onTriggerEventSelect(triggerEvent) {
      this.populateTriggerEventItems(triggerEvent)
    },

    populateTriggerEventItems(triggerEvent) {
      this.showDocumentTriggers = false

      if (triggerEvent === TriggerEventEnum.whenEmailTemplateTypeSent) {
        this.triggerEventItems = this.facilityEmailTemplateTypes.filter(x => x.id !== this.templateType.id).concat(this.defaultEmailTemplateType)
        this.triggerEventItemId = this.rule.trigger.data.emailTemplateTypeId || null
      }
      else if (triggerEvent === TriggerEventEnum.whenUserAddedDeleted) {
        this.showDocumentTriggers = true
        this.triggerEventItems = this.userTypes
        this.triggerEventItemId = this.triggerEventItems
          .find(x => x.accessLevel === this.rule.trigger.data.accessLevel && x.userType === this.rule.trigger.data.userType)
      }
      else if (triggerEvent === TriggerEventEnum.whenCalenderEventAddedDeleted) {
        this.showDocumentTriggers = true
        this.triggerEventItems = this.eventsTypesOptions
        this.triggerEventItemId = this.triggerEventItems.find(x => x.value === this.rule.trigger.data.type)
      }
      else if (triggerEvent === TriggerEventEnum.whenPremiumServiceAddedDeleted) {
        this.showDocumentTriggers = true
        this.triggerEventItems = this.premiumServiceTypesOptions
        this.triggerEventItemId = this.triggerEventItems.find(x => x.value === this.rule.trigger.data.type)
      }
      else {
        this.triggerEventItems = []
      }

      this.currentTriggerEvent = triggerEvent
    },

    onTriggerEventItemSelect(item) {
      if (this.rule.trigger.event === TriggerEventEnum.whenEmailTemplateTypeSent) {

        if (item.isDefault) {
          item = item.name
        }

        this.rule.trigger.data = {
          emailTemplateTypeId: item
        }

        this.triggerEventItemId = item

        this.rule.trigger.conditions = [{
          collection: 'emailTemplateTypes',
          type: 'string',
          property: 'emailTemplateTypeId',
          operator: '==',
          expectedValue: item
        }]
      }

      if (this.rule.trigger.event === TriggerEventEnum.whenUserAddedDeleted) {
        this.showDocumentTriggers = true

        this.rule.trigger.data = {
          userType: item.userType
        }

        this.rule.trigger.conditions = [{ collection: item.collection, type: 'string', property: 'userType', operator: '==', expectedValue: item.userType }]

        if (item.userType === UserTypeEnum.Staff) {
          this.rule.trigger.data.accessLevel = item.accessLevel
          this.rule.trigger.conditions = [{ collection: item.collection, type: 'string', property: 'accessLevel', operator: '==', expectedValue: item.accessLevel }, { collection: item.collection, type: 'string', property: 'userType', operator: '==', expectedValue: item.userType }]
        }
      }
      else if (this.rule.trigger.event === TriggerEventEnum.whenCalenderEventAddedDeleted) {
        this.showDocumentTriggers = true

        this.rule.trigger.data = { type: item.value }

        this.rule.trigger.conditions = [{ type: 'string', property: 'type', operator: '==', expectedValue: item.value }]
      }
      else if (this.rule.trigger.event === TriggerEventEnum.whenPremiumServiceAddedDeleted) {
        this.showDocumentTriggers = true

        this.rule.trigger.data = { type: item.value }

        this.rule.trigger.conditions = [{ type: 'string', property: 'type', operator: '==', expectedValue: item.value }]
      }
      else {
        this.showDocumentTriggers = false
      }
      console.log(JSON.stringify(this.rule))
    },

    onDocumentTriggerChange(triggerName) {
      if (this.rule.trigger.event === TriggerEventEnum.whenUserAddedDeleted
        || this.rule.trigger.event === TriggerEventEnum.whenCalenderEventAddedDeleted
        || this.rule.trigger.event === TriggerEventEnum.whenPremiumServiceAddedDeleted) {
        this.rule.trigger.data.documentTrigger = triggerName
        this.rule.trigger.conditions = this.rule.trigger.conditions.filter(x => x.property !== 'documentTrigger')
        this.rule.trigger.conditions.push({ type: 'string', property: 'documentTrigger', operator: '==', expectedValue: triggerName })
      }
    },


    fixString(str, cut) {
      return fixString(str, 25, cut)
    },

    cutString(str) {
      const text = str
      if (!text || !text.length) return ''

      return ((text.length > 25) ? text.slice(0, 22) + '...' : text)
    }

  }
};
</script>

<style scoped>
::v-deep .v-input--selection-controls__input {
  cursor: pointer;
}

.defaultTypeMark {
  background-color: gray;
  color: #ffffff;
  border-radius: 4px;
  padding: 1px 4px 1px 4px;
}
</style>